import { useState, useEffect, useCallback, useMemo } from 'react';
import { tefApi } from '../../lib/api';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';
import { toast } from '../../hooks/use-toast';
import { toast as siiToast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Search, ExternalLink, Lightbulb, Lock, Landmark, CheckCircle2, FileText, History } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { validarRutChileno, consultarSII } from '../../lib/rut';
import { NominaMenorDialog } from './NominaMenorDialog';
import { NominasMenoresHistorialDialog } from './NominasMenoresHistorialDialog';

const PAGE = 50;
const EMPTY = {
  rut_proveedor: '', rut_beneficiario: '', cuenta_beneficiario: '',
  banco_destino_cmf: '', email: '', tipo_cuenta: 'cta cte',
};

/** Cuentas bancarias de proveedores para el TEF — colección tef_cuentas. */
export function TefCuentasTab() {
  const { user } = useAuth();
  // Iter 292.k (Carlos, feb-2026) — sólo admin/gerencia puede editar el
  // "RUT beneficiario (titular)" cuando difiere del proveedor.
  const canEditBenef = user?.rol === 'admin' || user?.rol === 'gerencia';

  const [cuentas, setCuentas] = useState([]);
  const [total, setTotal] = useState(0);
  const [bancos, setBancos] = useState([]);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  // Iter 292.n (Carlos, feb-2026) — toggle "Sólo pendientes" (no cargadas al banco).
  const [soloPendientes, setSoloPendientes] = useState(false);
  // Iter 391 — Nómina menor TEF a proveedores (equivalente a "Genera Nómina" de Colaboradores)
  const [nominaMenorOpen, setNominaMenorOpen] = useState(false);
  const [historialMenorOpen, setHistorialMenorOpen] = useState(false);
  const [historialMenorKey, setHistorialMenorKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // {...cuenta, _isNew}
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  // Iter 292.m (Carlos, feb-2026) — bootstrap de cuentas en el banco (CPN.TXT, $1).
  const [generando, setGenerando] = useState(false);
  const [genResult, setGenResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tefApi.listCuentas({ q, limit: PAGE, skip: page * PAGE, solo_pendientes: soloPendientes });
      setCuentas(res.data.cuentas || []);
      setTotal(res.data.total || 0);
    } catch {
      toast({ title: 'Error al cargar cuentas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [q, page, soloPendientes]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { tefApi.listBancos().then((r) => setBancos(r.data.bancos || [])).catch(() => {}); }, []);

  const bancoLabel = (cmf) => {
    const b = bancos.find((x) => String(x.codigo_cmf) === String(cmf));
    return b ? `${b.codigo_cmf} · ${b.descripcion}` : (cmf || '—');
  };

  const abrirNuevo = () => setForm({ ...EMPTY, _isNew: true });
  const abrirEditar = (c) => setForm({ ...c, _isNew: false });

  const guardar = async () => {
    if (!form.rut_proveedor.trim() || !form.rut_beneficiario.trim()
        || !form.cuenta_beneficiario.trim() || !form.banco_destino_cmf) {
      toast({ title: 'RUT proveedor, RUT beneficiario, cuenta y banco son obligatorios', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        rut_proveedor: form.rut_proveedor.trim(),
        rut_beneficiario: form.rut_beneficiario.trim(),
        cuenta_beneficiario: form.cuenta_beneficiario.trim(),
        banco_destino_cmf: String(form.banco_destino_cmf),
        email: (form.email || '').trim(),
        tipo_cuenta: form.tipo_cuenta || 'cta cte',
      };
      if (form._isNew) await tefApi.crearCuenta(payload);
      else await tefApi.actualizarCuenta(form.id, payload);
      toast({ title: form._isNew ? 'Cuenta creada' : 'Cuenta actualizada' });
      setForm(null);
      load();
    } catch (e) {
      toast({ title: 'Error al guardar', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async () => {
    try {
      await tefApi.eliminarCuenta(delTarget.id);
      toast({ title: 'Cuenta eliminada' });
      setDelTarget(null);
      load();
    } catch (e) {
      toast({ title: 'Error al eliminar', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  // Iter 292.m — Generar CPN.TXT y descargar en la carpeta Descargas del navegador.
  const generarArchivoNuevas = async () => {
    setGenerando(true);
    try {
      const res = await tefApi.generarArchivoCuentasNuevas();
      const data = res.data || {};
      setGenResult(data);
      if (!data.archivo) {
        toast({ title: data.mensaje || 'No hay cuentas nuevas por cargar al banco.' });
        return;
      }
      // Decodificar base64 y disparar descarga como CPN.TXT
      const binStr = atob(data.archivo.contenido_b64);
      const bytes = new Uint8Array(binStr.length);
      for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'text/plain;charset=ISO-8859-1' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.archivo.filename || 'CPN.TXT';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({
        title: `CPN.TXT descargado`,
        description: `${data.num_lineas} cuenta(s) marcadas como cargadas al banco.`,
      });
      load();
    } catch (e) {
      toast({
        title: 'Error al generar el archivo',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setGenerando(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Buscar por razón social, RUT o email…"
                value={q}
                onChange={(e) => { setPage(0); setQ(e.target.value); }}
                data-testid="tef-cuentas-search"
              />
            </div>
            {/* Iter 292.n — toggle "Sólo pendientes" (no cargadas al banco). */}
            <button
              type="button"
              onClick={() => { setPage(0); setSoloPendientes((v) => !v); }}
              className={`text-xs px-2.5 py-1.5 rounded-md border transition ${
                soloPendientes
                  ? 'bg-amber-100 border-amber-400 text-amber-800 font-medium'
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
              title="Muestra sólo las cuentas que aún no se han cargado al banco"
              data-testid="tef-cuentas-solo-pendientes"
            >
              {soloPendientes ? '● Sólo pendientes' : '○ Sólo pendientes'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={generarArchivoNuevas}
              disabled={generando}
              className="gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50"
              title="Genera un CPN.TXT con las cuentas aún no cargadas (monto $1) para dar de alta en el Banco"
              data-testid="tef-cuenta-generar-cpn"
            >
              {generando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Landmark className="h-4 w-4" />}
              Crea nuevas cuentas en Banco
            </Button>
            <Button
              size="sm" onClick={() => setNominaMenorOpen(true)}
              className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
              title="Genera un TEF del Banco de Chile para pagos sueltos a proveedores (facturas, boletas de honorarios, otros)"
              data-testid="tef-nomina-menor"
            >
              <FileText className="h-4 w-4" /> Nómina menor TEF
            </Button>
            <Button
              size="sm" variant="ghost" onClick={() => setHistorialMenorOpen(true)}
              className="gap-1.5 text-slate-700" title="Nóminas menores generadas, con re-descarga del archivo"
              data-testid="tef-nomina-menor-historial"
            >
              <History className="h-4 w-4" /> Historial
            </Button>
            <Button size="sm" onClick={abrirNuevo} className="gap-1.5" data-testid="tef-cuenta-nueva">
              <Plus className="h-4 w-4" /> Nueva cuenta
            </Button>
          </div>
        </div>

        {genResult && genResult.omitidas?.length > 0 && (
          <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2" data-testid="tef-cuenta-cpn-omitidas">
            <b>{genResult.omitidas.length}</b> cuenta(s) quedaron fuera del CPN.TXT: revisa la configuración de &quot;Datos Banco&quot;.
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        ) : (
          <>
            <Table data-testid="tef-cuentas-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Proveedor (razón social)</TableHead>
                  <TableHead className="w-32">RUT proveedor</TableHead>
                  <TableHead className="w-32">RUT beneficiario</TableHead>
                  <TableHead>Cuenta</TableHead>
                  <TableHead>Banco</TableHead>
                  <TableHead className="w-20">Tipo</TableHead>
                  <TableHead className="w-24">Estado banco</TableHead>
                  <TableHead className="w-24 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cuentas.map((c) => (
                  <TableRow key={c.id} data-testid={`tef-cuenta-row-${c.id}`}>
                    <TableCell className="max-w-[16rem]">
                      {c.nombre_proveedor
                        ? <span className="truncate block" title={c.nombre_proveedor} data-testid={`tef-cuenta-nombre-${c.id}`}>{c.nombre_proveedor}</span>
                        : <span className="text-muted-foreground italic text-xs">Sin razón social</span>}
                    </TableCell>
                    <TableCell className="font-mono">{c.rut_proveedor}</TableCell>
                    <TableCell className="font-mono">{c.rut_beneficiario}</TableCell>
                    <TableCell className="font-mono">{c.cuenta_beneficiario}</TableCell>
                    <TableCell className="text-xs">{bancoLabel(c.banco_destino_cmf)}</TableCell>
                    <TableCell className="text-xs">{c.tipo_cuenta || 'cta cte'}</TableCell>
                    <TableCell className="text-xs">
                      {c.cargada_al_banco ? (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-green-100 text-green-700 border border-green-300"
                          title={`Cargada en el banco por ${c.cargada_al_banco_por || '?'} el ${c.cargada_al_banco_en || '?'}`}
                          data-testid={`tef-cuenta-cargada-${c.id}`}
                        >
                          <CheckCircle2 className="h-3 w-3" /> Cargada
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-300" data-testid={`tef-cuenta-pendiente-${c.id}`}>
                          Pendiente
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => abrirEditar(c)} data-testid={`tef-cuenta-edit-${c.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => setDelTarget(c)} data-testid={`tef-cuenta-del-${c.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {cuentas.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">Sin cuentas.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span data-testid="tef-cuentas-total">{total} cuenta(s)</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="tef-cuentas-prev">Anterior</Button>
                <span>Página {page + 1} / {totalPages}</span>
                <Button size="sm" variant="outline" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="tef-cuentas-next">Siguiente</Button>
              </div>
            </div>
          </>
        )}
      </CardContent>

      {/* Iter 391 — Nómina menor TEF a proveedores + historial */}
      <NominaMenorDialog open={nominaMenorOpen} onClose={() => setNominaMenorOpen(false)} onGenerated={() => setHistorialMenorKey((k) => k + 1)} />
      <NominasMenoresHistorialDialog open={historialMenorOpen} onClose={() => setHistorialMenorOpen(false)} refreshKey={historialMenorKey} />

      {/* Dialog crear/editar */}
      <Dialog open={!!form} onOpenChange={(o) => !o && !saving && setForm(null)}>
        <DialogContent className="max-w-lg" data-testid="tef-cuenta-dialog">
          <DialogHeader>
            <DialogTitle>{form?._isNew ? 'Nueva cuenta de proveedor' : 'Editar cuenta'}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label>RUT proveedor (para el cruce con la factura)</Label>
                {form._isNew ? (
                  <ProveedorSearchInline
                    onSelect={(prov) => setForm((f) => ({
                      ...f,
                      rut_proveedor: prov.rut,
                      // Iter 292.k — beneficiario = proveedor por defecto.
                      rut_beneficiario: f.rut_beneficiario || prov.rut,
                    }))}
                    valueRut={form.rut_proveedor}
                  />
                ) : (
                  <>
                    <Input value={form.rut_proveedor} readOnly disabled className="bg-muted" data-testid="tef-cuenta-rutprov" />
                    <p className="text-xs text-muted-foreground" data-testid="tef-cuenta-dialog-nombre">
                      Razón social: <span className="font-medium text-foreground">{form.nombre_proveedor || 'Sin razón social en el maestro'}</span>
                    </p>
                  </>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5">
                  RUT beneficiario (titular)
                  {!canEditBenef && <Lock className="h-3 w-3 text-muted-foreground" title="Sólo admin/gerencia puede modificarlo" />}
                </Label>
                <Input
                  value={form.rut_beneficiario}
                  onChange={(e) => setForm({ ...form, rut_beneficiario: e.target.value })}
                  placeholder="76822619-9"
                  disabled={!canEditBenef}
                  className={!canEditBenef ? 'bg-muted' : ''}
                  data-testid="tef-cuenta-rutbenef"
                />
                {!canEditBenef && (
                  <p className="text-[10px] text-muted-foreground">Sólo administradores o gerencia pueden cambiar el titular.</p>
                )}
                {canEditBenef && form.rut_beneficiario && form.rut_beneficiario !== form.rut_proveedor && (
                  <p className="text-[10px] text-amber-700">⚠️ El titular difiere del proveedor de la factura.</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>N° cuenta</Label>
                <Input value={form.cuenta_beneficiario} onChange={(e) => setForm({ ...form, cuenta_beneficiario: e.target.value })} placeholder="00012345678" data-testid="tef-cuenta-numero" />
              </div>
              <div className="space-y-1.5">
                <Label>Banco destino</Label>
                <Select value={String(form.banco_destino_cmf || '')} onValueChange={(v) => setForm({ ...form, banco_destino_cmf: v })}>
                  <SelectTrigger data-testid="tef-cuenta-banco"><SelectValue placeholder="Elige un banco" /></SelectTrigger>
                  <SelectContent>
                    {bancos.map((b) => (
                      <SelectItem key={b.codigo_cmf} value={String(b.codigo_cmf)}>{b.codigo_cmf} · {b.descripcion}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tipo de cuenta</Label>
                <Select value={form.tipo_cuenta || 'cta cte'} onValueChange={(v) => setForm({ ...form, tipo_cuenta: v })}>
                  <SelectTrigger data-testid="tef-cuenta-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cta cte">Cta corriente (CTD)</SelectItem>
                    <SelectItem value="cta vista">Cta vista (JUV)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Email de notificación (opcional)</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="pagos@proveedor.cl" data-testid="tef-cuenta-email" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)} disabled={saving}>Cancelar</Button>
            <Button onClick={guardar} disabled={saving} className="gap-1.5" data-testid="tef-cuenta-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar borrado */}
      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="tef-cuenta-del-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar cuenta</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar la cuenta del RUT {delTarget?.rut_proveedor}? Las facturas de ese proveedor
              se omitirán al generar el TEF hasta que registres una cuenta nueva.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminar} className="bg-red-600 hover:bg-red-700" data-testid="tef-cuenta-del-confirm">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
// Iter 292.k (Carlos, feb-2026) — Buscador de proveedores con creación inline.
// Mismo patrón que Generar OC → nuevo proveedor: search + lista + "crear en
// Alegra" si no existe. Validación módulo 11 + link SII.
// ═════════════════════════════════════════════════════════════════════════════
function ProveedorSearchInline({ onSelect, valueRut }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selected, setSelected] = useState(null); // { rut, nombre }

  // Modo "crear nuevo" cuando no aparece en la búsqueda
  const [nuevoRut, setNuevoRut] = useState('');
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [creando, setCreando] = useState(false);

  const rutInvalido = useMemo(
    () => nuevoRut.trim() !== '' && !validarRutChileno(nuevoRut.trim()),
    [nuevoRut],
  );

  const buscar = async () => {
    if (!q.trim()) return;
    setLoading(true);
    setSearched(false);
    try {
      const res = await api.get(`/compras/proveedores-lista?q=${encodeURIComponent(q)}`);
      setResults(res.data.proveedores || []);
      setSearched(true);
    } catch {
      toast({ title: 'Error buscando proveedores', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const elegir = (p) => {
    const rut = p.rut || p.identificacion || '';
    const nombre = p.nombre || '';
    setSelected({ rut, nombre });
    setResults([]);
    setSearched(false);
    onSelect({ rut, nombre });
  };

  const crearEnAlegra = async () => {
    const rut = nuevoRut.trim();
    const nombre = nuevoNombre.trim();
    if (!rut || !nombre) {
      toast({ title: 'RUT y razón social son obligatorios', variant: 'destructive' });
      return;
    }
    if (!validarRutChileno(rut)) {
      toast({ title: 'Error en RUT. Por favor, revíselo y vuelva a ingresarlo.', variant: 'destructive' });
      return;
    }
    setCreando(true);
    try {
      const res = await api.post('/compras/proveedores/crear-en-alegra', { rut, nombre });
      const prov = res.data?.proveedor;
      if (prov) {
        toast({ title: res.data?.message || `Proveedor creado en Alegra: ${prov.nombre}` });
        setSelected({ rut: prov.rut, nombre: prov.nombre });
        setResults([]);
        setSearched(false);
        setNuevoRut('');
        setNuevoNombre('');
        onSelect({ rut: prov.rut, nombre: prov.nombre });
      }
    } catch (err) {
      toast({
        title: 'Error al crear proveedor',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setCreando(false);
    }
  };

  // Si ya hay seleccionado, mostrar chip con botón "Cambiar"
  if (selected || valueRut) {
    return (
      <div className="flex items-center gap-2 border rounded-md p-2 bg-green-50/30 border-green-300" data-testid="tef-cuenta-prov-selected">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{selected?.nombre || '—'}</p>
          <p className="text-xs font-mono text-gray-600">{selected?.rut || valueRut}</p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => { setSelected(null); onSelect({ rut: '', nombre: '' }); setQ(''); }}
          data-testid="tef-cuenta-prov-cambiar"
        >
          Cambiar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          placeholder="Buscar por nombre o RUT del proveedor…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), buscar())}
          data-testid="tef-cuenta-prov-search"
        />
        <Button
          type="button"
          onClick={buscar}
          disabled={loading}
          variant="outline"
          data-testid="tef-cuenta-prov-search-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>

      {results.length > 0 && (
        <div className="max-h-56 overflow-y-auto border rounded" data-testid="tef-cuenta-prov-results">
          {results.map((p, i) => {
            const rutKey = p.rut || p.identificacion;
            return (
              <button
                key={i}
                type="button"
                onClick={() => elegir(p)}
                className="w-full border-b last:border-b-0 text-left px-3 py-2 text-sm hover:bg-blue-50"
                data-testid={`tef-cuenta-prov-result-${rutKey}`}
              >
                <div className="font-medium">{p.nombre}</div>
                <div className="text-[11px] text-gray-500 font-mono">{rutKey}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Sin resultados → crear en Alegra (mismo patrón que Generar OC) */}
      {searched && results.length === 0 && (
        <div className="border rounded p-3 bg-amber-50/50 border-amber-200" data-testid="tef-cuenta-crear-prov-box">
          <p className="text-sm text-amber-900 font-medium flex items-center gap-1">
            <Lightbulb className="w-4 h-4" /> No se encontró el proveedor
          </p>
          <p className="text-xs text-gray-600 mt-0.5">
            Créalo en Alegra (fuente de verdad) antes de registrar su cuenta TEF.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 mt-2 sm:items-start">
            <div className="sm:max-w-[180px] w-full">
              <Input
                placeholder="RUT (ej. 76123456-7)"
                value={nuevoRut}
                onChange={(e) => setNuevoRut(e.target.value)}
                className={rutInvalido ? 'border-red-500 focus-visible:ring-red-500' : ''}
                aria-invalid={rutInvalido}
                data-testid="tef-cuenta-nuevo-prov-rut"
              />
              {rutInvalido && (
                <p className="text-xs text-red-600 mt-1" data-testid="tef-cuenta-nuevo-prov-rut-error">
                  Error en RUT. Por favor, revíselo y vuelva a ingresarlo.
                </p>
              )}
              <button
                type="button"
                onClick={() => consultarSII(nuevoRut, siiToast)}
                className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1"
                data-testid="tef-cuenta-consultar-sii"
              >
                <ExternalLink className="w-3 h-3" /> Consultar situación tributaria en SII
              </button>
            </div>
            <Input
              placeholder="Razón social"
              value={nuevoNombre}
              onChange={(e) => setNuevoNombre(e.target.value)}
              className="flex-1"
              data-testid="tef-cuenta-nuevo-prov-nombre"
            />
            <Button
              type="button"
              onClick={crearEnAlegra}
              disabled={creando || rutInvalido}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="tef-cuenta-crear-prov-btn"
            >
              {creando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear en Alegra'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
